<template>
  <div class="modal-overlay" v-show="isVisible" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          {{ isEditMode ? this.$t('edit_promo_code') : this.$t('create_promo_code') }}
        </h2>
        <button class="modal-close" @click="handleClose">
          <img src="../../assets/images/cross.svg" alt="Close">
        </button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="handleSubmit" class="create-form">

          <div class="form-section">

            <div class="form-group">
              <label class="form-label">{{ $t('module_type') }} *</label>
              <SelectBox
                  v-model="form.moduleUuid"
                  :options="moduleTypeOptions"
                  :placeholder="$t('select_module_type')"
                  :class="{ 'input-error': errors.moduleUuid }"
              />
              <span v-if="errors.moduleUuid" class="error-message">{{ errors.moduleUuid }}</span>
            </div>

            <div class="form-group" v-if="form.moduleUuid">
              <label class="form-label">{{ $t('select_product') }} *</label>
              <SelectBox
                  v-model="form.productUuid"
                  :options="productOptions"
                  :placeholder="$t('select_the_product')"
                  :disabled="loadingProducts"
                  :class="{ 'input-error': errors.productUuid }"
              />
              <p v-if="loadingProducts" class="loading-text">{{$t('products_loading')}}...</p>
              <span v-if="errors.productUuid" class="error-message">{{ errors.productUuid }}</span>
            </div>
          </div>

          <div class="form-section">
            <div class="form-group">
              <label class="form-label">{{$t('code')}} *</label>
              <input
                  v-model="form.code"
                  type="text"
                  class="form-input"
                  :class="{ 'input-error': errors.code }"
                  :placeholder="$t('enter_the_code') + ' (SUMMER40..)'"
                  minlength="3"
                  maxlength="50"
              />
              <span v-if="errors.code" class="error-message">{{ errors.code }}</span>
            </div>
          </div>

          <div class="form-section">

            <div class="form-group">
              <label class="form-label">{{ $t('discount_rate') }} (%) *</label>
              <input
                  v-model="form.promotionPercentage"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  class="form-input"
                  :class="{ 'input-error': errors.promotionPercentage }"
                  :placeholder="$t('enter_the_discount_rate')+'(0-100)'"
              />
              <span v-if="errors.promotionPercentage" class="error-message">{{ errors.promotionPercentage }}</span>
            </div>
          </div>

          <div class="form-section">

            <div class="form-group">
              <label class="form-label">{{$t('stock_quantity')}} *</label>
              <input
                  v-model="form.count"
                  type="number"
                  step="1"
                  min="1"
                  max="999999"
                  class="form-input"
                  :class="{ 'input-error': errors.count }"
                  :placeholder="this.$t('how_many_codes_you_want_to_create')"
              />
              <span v-if="errors.count" class="error-message">{{ errors.count }}</span>
            </div>
          </div>

          <div class="form-section">

            <div class="date-row">
              <div class="form-group">
                <label class="form-label">{{ $t('start_date') }} *</label>
                <input
                    v-model="form.startDate"
                    type="datetime-local"
                    class="form-input"
                    :class="{ 'input-error': errors.startDate }"
                />
                <span v-if="errors.startDate" class="error-message">{{ errors.startDate }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('end_date') }} *</label>
                <input
                    v-model="form.endDate"
                    type="datetime-local"
                    class="form-input"
                    :class="{ 'input-error': errors.endDate }"
                />
                <span v-if="errors.endDate" class="error-message">{{ errors.endDate }}</span>
              </div>
            </div>
          </div>

        </form>
      </div>

      <div class="modal-footer">
        <button
            type="button"
            @click="handleClose"
            class="dark-button-sm"
        >
          {{ $t('cancel') }}
        </button>
        <button
            type="button"
            @click="handleSubmit"
            :disabled="isSubmitting"
            class="primary-button"
        >
          <span v-if="isSubmitting">
            {{ isEditMode ? this.$t('updating')+'...' : this.$t('creating')+'...' }}
          </span>
          <span v-else>
            {{ isEditMode ? this.$t('update') : this.$t('create_promo_code') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SelectBox from "@/components/SelectBox.vue";
import marketplaceService from "@/service/marketplaceService";
import marketplaceModuleService from "@/service/marketplaceModuleService";
import promotionCodeService from "@/service/promotionCodeService";
import { handleErrorResponse } from "@/utils/utils";

export default {
  name: 'PromotionCodeModal',
  components: {
    SelectBox
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    editItem: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        moduleUuid: '',
        productUuid: '',
        code: '',
        promotionPercentage: '',
        startDate: '',
        endDate: '',
        count: ''
      },

      errors: {
        moduleUuid: '',
        productUuid: '',
        code: '',
        promotionPercentage: '',
        startDate: '',
        endDate: '',
        count: ''
      },

      moduleTypeOptions: [],
      productOptions: [],

      loadingProducts: false,
      isSubmitting: false,

      moduleTypeMapping: {
        '5d922f3f-5d9b-4d5c-8880-d0c90e6e5d18': 'test',
        '37f57ccf-6486-4de7-b6b7-bd326cd3a561': 'program',
        '7c008460-9e4b-41eb-96e6-c5d4b1222189': 'gamelabs'
      }
    }
  },
  computed: {
    isEditMode() {
      return this.editItem !== null;
    }
  },
  watch: {
    isVisible: {
      handler(newValue) {
        if (newValue) {
          this.initializeModal();
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      },
      immediate: true
    },
    'form.moduleUuid'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.onModuleTypeChange();
      }
    },
    editItem: {
      handler(newValue) {
        if (newValue && this.isVisible) {
          this.populateFormForEdit();
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
  methods: {
    async initializeModal() {
      await this.loadModuleTypes();

      if (this.isEditMode) {
        await this.populateFormForEdit();
      } else {
        this.resetForm();
        this.setDefaultDates();
      }
    },

    async populateFormForEdit() {
      if (!this.editItem) return;

      this.form = {
        moduleUuid: this.editItem.moduleUuid || '',
        productUuid: '',
        code: this.editItem.code || '',
        promotionPercentage: this.editItem.promotionPercentage || '',
        startDate: this.formatDateForInput(this.editItem.startDate),
        endDate: this.formatDateForInput(this.editItem.endDate),
        count: this.editItem.count || ''
      };

      if (this.form.moduleUuid) {
        await this.loadProducts();

        const targetProductUuid = this.editItem.productUuid || '';
        this.form.productUuid = targetProductUuid;
      }
    },

    resetForm() {
      this.form = {
        moduleUuid: '',
        productUuid: '',
        code: '',
        promotionPercentage: '',
        startDate: '',
        endDate: '',
        count: ''
      };
      this.clearErrors();
    },

    clearErrors() {
      this.errors = {
        moduleUuid: '',
        productUuid: '',
        code: '',
        promotionPercentage: '',
        startDate: '',
        endDate: '',
        count: ''
      };
    },

    validateForm() {
      this.clearErrors();
      let isValid = true;

      // moduleUuid - Zorunlu
      if (!this.form.moduleUuid) {
        this.errors.moduleUuid = this.$t('validation_module_required');
        isValid = false;
      }

      // productUuid - Zorunlu
      if (!this.form.productUuid) {
        this.errors.productUuid = this.$t('validation_product_required');
        isValid = false;
      }

      // code - Zorunlu, 3-50 karakter
      if (!this.form.code) {
        this.errors.code = this.$t('validation_code_required');
        isValid = false;
      } else if (this.form.code.length < 3) {
        this.errors.code = this.$t('validation_code_min_length');
        isValid = false;
      } else if (this.form.code.length > 50) {
        this.errors.code = this.$t('validation_code_max_length');
        isValid = false;
      }

      // promotionPercentage - Zorunlu, 0-100 arası
      const percentage = parseInt(this.form.promotionPercentage);
      if (this.form.promotionPercentage === '' || this.form.promotionPercentage === null) {
        this.errors.promotionPercentage = this.$t('validation_discount_required');
        isValid = false;
      } else if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        this.errors.promotionPercentage = this.$t('validation_discount_range');
        isValid = false;
      }

      // count - Zorunlu, 1-999999 arası
      const count = parseInt(this.form.count);
      if (this.form.count === '' || this.form.count === null) {
        this.errors.count = this.$t('validation_count_required');
        isValid = false;
      } else if (isNaN(count) || count < 1 || count > 999999) {
        this.errors.count = this.$t('validation_count_range');
        isValid = false;
      }

      // startDate - Zorunlu
      if (!this.form.startDate) {
        this.errors.startDate = this.$t('validation_start_date_required');
        isValid = false;
      }

      // endDate - Zorunlu, startDate'den sonra olmalı
      if (!this.form.endDate) {
        this.errors.endDate = this.$t('validation_end_date_required');
        isValid = false;
      } else if (this.form.startDate && new Date(this.form.endDate) <= new Date(this.form.startDate)) {
        this.errors.endDate = this.$t('validation_end_date_after_start');
        isValid = false;
      }

      return isValid;
    },

    async loadModuleTypes() {
      try {
        const response = await marketplaceModuleService.store({
          page: 1,
          perPage: 100
        });

        const modules = response.data?.data?.data || response.data?.data || [];

        this.moduleTypeOptions = modules.map(module => ({
          value: module.uuid,
          text: module.title || module.moduleName
        }));

      } catch (error) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t('failed_to_load_modules')
        });
      }
    },

    async onModuleTypeChange() {
      this.form.productUuid = '';
      this.productOptions = [];

      if (this.form.moduleUuid) {
        await this.loadProducts();
      }
    },

    async loadProducts() {
      const moduleType = this.moduleTypeMapping[this.form.moduleUuid];

      if (!moduleType) return;

      try {
        this.loadingProducts = true;
        const response = await marketplaceService.myProduct({
          'moduleId': this.form.moduleUuid,
        });

        const products = response.data.data?.data || response.data?.data || [];

        this.productOptions = products.map((product, index) => {
          const value = product.uuid ||
              product.programId ||
              product.testId ||
              product.gameLabsId ||
              product.id ||
              `product_${index}`;

          let text;
          switch (moduleType) {
            case 'test':
              text = product.testName || product.name || product.title || `Test ${index + 1}`;
              break;
            case 'program':
              text = product.name || product.title || product.programName || `Program ${index + 1}`;
              break;
            case 'gamelabs':
              text = product.name || product.title || product.gameLabName || `GameLab ${index + 1}`;
              break;
            default:
              text = product.title || product.name || `Product ${index + 1}`;
              break;
          }

          return { value, text };
        });

      } catch (error) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t('failed_to_load_products')
        });
      } finally {
        this.loadingProducts = false;
      }
    },

    async searchProducts(searchText) {
      await this.loadProducts();
    },

    setDefaultDates() {
      const now = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);
      this.form.startDate = this.formatDateForInput(now);
      this.form.endDate = this.formatDateForInput(endDate);
    },

    formatDateForInput(dateInput) {
      if (!dateInput) return '';

      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    formatDateForAPI(dateString) {
      return dateString.replace('T', ' ') + ':00';
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.isSubmitting = true;

        const payload = {
          moduleUuid: this.form.moduleUuid,
          code: this.form.code,
          promotionPercentage: parseInt(this.form.promotionPercentage),
          productUuid: this.form.productUuid,
          startDate: this.formatDateForAPI(this.form.startDate),
          endDate: this.formatDateForAPI(this.form.endDate),
          count: parseInt(this.form.count)
        };

        let response;
        if (this.isEditMode) {
          payload.uuid = this.editItem.uuid || this.editItem.id;
          response = await promotionCodeService.updatePromotionCode(payload);
        } else {
          response = await promotionCodeService.createPromotionCode(payload);
        }

        if (this.isEditMode) {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('promo_code_updated_successfully')
          });
        }

        const promotionData = {
          code: this.form.code,
          promotionCode: this.form.code,
          ...response.data?.data
        };

        this.$emit('success', promotionData);
        this.handleClose();

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.isSubmitting = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },

    handleOverlayClick() {
      this.handleClose();
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  max-width: 800px;
  max-height: 90vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #1c1d1f;
  margin: 0;
}

.modal-close {
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  height: 36px;
  width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-close img {
  width: 16px;
  height: 16px;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 24px 32px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1c1d1f;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title::before {
  content: '';
  width: 3px;
  height: 16px;
  background: #5624d0;
  border-radius: 2px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1c1d1f;
  margin-bottom: 6px;
}

.date-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.dark-button-sm, .primary-button {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.loading-text {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
  font-style: italic;
}

.error-message {
  display: block;
  font-size: 12px;
  color: #dc3545;
  margin-top: 4px;
}

.input-error {
  border-color: #dc3545 !important;
}

.form-input.input-error {
  border: 1px solid #dc3545;
}

.form-input.input-error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.1);
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 20px;
  }

  .date-row {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-direction: column;
  }
}
</style>
